import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, Skeleton, Tooltip, Typography } from "@/components/atoms";
import { HowPricingWorksButton, HowPricingWorksModal, useCustomRatesContext } from "@/components/organisms";
import { useCountryConfig, useOperator } from "@/hooks";
import { DynamicPricingState } from "@/redux/slices/trip/types";
import { clsx } from "@/utils";
import { StateArtwork, hasStateArtwork } from "./stateArtwork";
import { StateRates } from "./StateRates";
import { StateRatesEdit } from "./StateRatesEdit";

export const CustomRates = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const { isFetching, states } = useCustomRatesContext();
  const { country } = useOperator();
  const distanceUnit = states[0]?.distanceUnit ?? "kilometer";
  // Card grid where we have region artwork, table otherwise — see stateArtwork.tsx.
  const useTableView = !hasStateArtwork(country);

  if (isFetching) {
    return (
      <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
        <Skeleton />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-120px)] flex-1 flex-col px-5 py-4">
      <div className="flex flex-col justify-between gap-4 xl:flex-row">
        <div className="flex flex-col gap-1">
          <Typography variant="h2">Custom Rates</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            Pick a state to set prices by {distanceUnit} distance. State without custom prices will use the default rates instead.
          </Typography>
        </div>
        <div>
          <HowPricingWorksButton onClick={() => setOpenModal(true)} />
        </div>
      </div>
      {useTableView ? (
        <StateTable states={states} onNavigate={(slug) => navigate(slug)} />
      ) : (
        <StateCardGrid states={states} onNavigate={(slug) => navigate(slug)} />
      )}
      <HowPricingWorksModal open={openModal} onClose={() => setOpenModal(false)} />
    </div>
  );
};

interface StateListProps {
  states: DynamicPricingState[];
  onNavigate: (slug: string) => void;
}

const StateTable = ({ states, onNavigate }: StateListProps) => {
  const { country } = useOperator();
  const { config } = useCountryConfig(country);
  const [search, setSearch] = useState("");
  const filtered = search
    ? states.filter((s) => s.name.toLowerCase().includes(search.toLowerCase()))
    : states;

  return (
    <div className="mt-4 flex flex-col gap-3">
      <input
        type="text"
        placeholder="Search states..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full max-w-sm rounded-lg border border-neutral-mid-gray px-3 py-2 text-sm focus:border-primary focus:outline-none"
      />
      <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-mid-gray bg-neutral-surface-gray">
              <th className="px-4 py-3 text-left text-sm font-medium text-neutral-dark-gray">{config.regionLabel}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-neutral-dark-gray">Pricing</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-neutral-dark-gray">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((state) => {
              const { managed, baseInterval, state: slug } = state;
              const isDefault = !managed && !baseInterval;
              return (
                <tr
                  key={state.id}
                  className={clsx(
                    "border-b border-neutral-mid-gray last:border-b-0 bg-white transition-colors",
                    !managed && "hover:bg-neutral-surface-gray cursor-pointer"
                  )}
                  onClick={() => (!managed ? onNavigate(slug) : undefined)}
                >
                  <td className="px-4 py-3">
                    <Typography variant="action">{state.name}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    {managed ? (
                      <div className="flex items-center gap-1.5 text-neutral-dark-gray">
                        <Tooltip content="Pricing is managed by RideMinder. Please contact RideMinder to make changes" placement="top">
                          <Icon name="Lock1" size="sm" />
                        </Tooltip>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Managed
                        </Typography>
                      </div>
                    ) : isDefault ? (
                      <Typography variant="paragraph" className="text-neutral-dark-gray">
                        Using Default Pricing
                      </Typography>
                    ) : (
                      <div className="flex items-center gap-1.5 text-neutral-dark-gray">
                        <Icon size="sm" name="Setting5" className="flex-shrink-0" />
                        <Typography variant="paragraph">Using Custom Pricing</Typography>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!managed && (
                      <Typography variant="action" className="text-info">
                        {isDefault ? "Set Custom Prices" : "Edit Custom Prices"}
                      </Typography>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-neutral-dark-gray">
                  No states found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StateCardGrid = ({ states, onNavigate }: StateListProps) => (
  <div className="mt-4 flex flex-col items-center gap-5 xl:flex-row xl:items-stretch">
    <ul role="list" className="mt-5 grid w-full grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-5">
      {states.map((state) => {
        const { managed, baseInterval, state: slug, id } = state;
        const isDefault = !managed && !baseInterval;
        return (
          <li
            key={id}
            className={clsx(
              "flex min-w-0 flex-col gap-1 rounded-lg border border-neutral-mid-gray bg-white p-2.5",
              managed ? "cursor-default" : "cursor-pointer"
            )}
            onClick={() => (managed ? null : onNavigate(slug))}
          >
            <StateDisplay state={state} />
            <Typography variant="action" className="text-neutral-dark-gray">
              {state.name}
            </Typography>
            {isDefault ? (
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                Using Default Pricing
              </Typography>
            ) : (
              <div className="flex flex-row items-center gap-x-1.5 text-neutral-dark-gray">
                <Icon size="md" name="Setting5" className="flex-shrink-0" />
                <Typography variant="paragraph">Using Custom Pricing</Typography>
              </div>
            )}
            {managed ? (
              <div className="flex">
                <div>
                  <Tooltip content="Pricing is managed by RideMinder. Please contact RideMinder to make changes" placement="top">
                    <Icon name="Lock1" size="sm" className="text-neutral-dark-gray" />
                  </Tooltip>
                </div>
              </div>
            ) : (
              <Typography variant="action" className="text-info">
                {isDefault ? "Set Custom Prices" : "Edit Custom Prices"}
              </Typography>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

interface StateDisplayProps {
  state: DynamicPricingState;
}

const StateDisplay = ({ state }: StateDisplayProps) => {
  const { managed, baseInterval, id } = state;
  const isDefault = !managed && !baseInterval;
  const className = clsx(
    "block w-full h-auto min-h-[150px] shrink-0",
    managed || isDefault
      ? "[--state-fill-color:theme(colors.primary.light)] [--state-stroke-color:theme(colors.primary.DEFAULT)]"
      : "[--state-fill-color:theme(colors.orange.light)] [--state-stroke-color:theme(colors.orange.DEFAULT)]"
  );

  return <StateArtwork slug={id} className={className} fallbackClassName={clsx(className, "rounded-md bg-neutral-gray")} />;
};

CustomRates.StateRates = StateRates;
CustomRates.StateRatesEdit = StateRatesEdit;
